import {useState, useEffect, useCallback} from 'react';

import {EMPTY_ARRAY} from '~/shared/consts/commonConsts';
import {IMoneycardsRes} from '~/shared/services/bffService/api/interfaces/responses';
import {handleRefreshToken} from '~/shared/services/auth';
import {ApiError, is401Error} from '~/shared/services/apiErrorService';
import {retryApiRequest} from '~/shared/services/apiService/apiServiceUtils';
import {StateAsyncProperty} from '~/shared/store/redux-toolbelt-10bis';

interface UseMoneyCardsParams {
  autoFetch?: boolean;
  fetchMoneyCards?: () => Promise<IMoneycardsRes>;
}

interface UseMoneyCardsReturn extends StateAsyncProperty<IMoneycardsRes> {
  refetch: () => Promise<void>;
  tenbisCreditBalance: number;
}

const useMoneyCards = ({autoFetch = true, fetchMoneyCards}: UseMoneyCardsParams): UseMoneyCardsReturn => {
  const [moneyCards, setMoneyCards] = useState<StateAsyncProperty<IMoneycardsRes>>({
    data: EMPTY_ARRAY as IMoneycardsRes,
    loading: false,
    loaded: false,
    error: undefined,
  });

  const _fetchMoneyCards = useCallback(async () => {
    setMoneyCards(prev => ({...prev, loaded: false, loading: true, error: undefined}));

    try {
      const response = await retryApiRequest(async () => {
        const result = await fetchMoneyCards?.();
        return result;
      });
      setMoneyCards({
        data: response || EMPTY_ARRAY,
        loading: false,
        loaded: true,
        error: undefined,
      });
    } catch (error) {
      if (is401Error(error)) {
        await handleRefreshToken(error, _fetchMoneyCards);
        return;
      }
      
      setMoneyCards({
        data: EMPTY_ARRAY as IMoneycardsRes,
        loading: false,
        loaded: true,
        error: error as ApiError,
      });
    }
  }, [fetchMoneyCards]);

  useEffect(() => {
    if (autoFetch) {
      _fetchMoneyCards();
    }
  }, [autoFetch, _fetchMoneyCards]);

  const tenbisCreditBalance = moneyCards.data?.find(card => card.isTenbisCredit)?.balance || 0;

  return {
    ...moneyCards,
    refetch: _fetchMoneyCards,
    tenbisCreditBalance,
  };
};

export default useMoneyCards;
